import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errText, mediaUrl } from "../lib/api";
import { Avatar, CreatorName, formatNum, Loading, renderMentions } from "../components/common";
import { TipModal } from "../components/TipModal";
import { MentionInput } from "../components/MentionInput";
import { GifPicker } from "../components/GifPicker";
import { Heart, MessageCircle, Repeat2, Coins, Music2, ArrowLeft, Volume2, VolumeX, Home, PlusCircle, X, Send, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function Reels() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const nav = useNavigate();

  useEffect(() => { api.get("/reels").then(({ data }) => setReels(data)).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="min-h-screen bg-black"><Loading /></div>;

  return (
    <div className="fixed inset-0 bg-black overflow-y-auto snap-y snap-mandatory hide-scroll" data-testid="reels-container">
      <div className="fixed top-4 left-4 z-30 flex gap-2">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full glass flex items-center justify-center" data-testid="reels-back"><ArrowLeft size={18} /></button>
        <Link to="/home" className="w-10 h-10 rounded-full glass flex items-center justify-center"><Home size={18} /></Link>
      </div>
      <button onClick={() => setMuted(!muted)} className="fixed top-4 right-4 z-30 w-10 h-10 rounded-full glass flex items-center justify-center">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
      {reels.map((r) => <ReelItem key={r.id} reel={r} muted={muted} />)}
    </div>
  );
}

function ReelItem({ reel, muted }) {
  const [r, setR] = useState(reel);
  const [tipOpen, setTipOpen] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [cText, setCText] = useState("");
  const [cGif, setCGif] = useState(null);
  const [gifOpen, setGifOpen] = useState(false);
  const vref = useRef(); const wrap = useRef(); const viewed = useRef(false);

  const openComments = () => {
    setShowComments(true);
    if (comments === null) api.get(`/reels/${r.id}/comments`).then(({ data }) => setComments(data)).catch(() => setComments([]));
  };
  const addComment = async () => {
    if (!cText.trim() && !cGif) return;
    try {
      const { data } = await api.post(`/reels/${r.id}/comment`, { text: cText, gif_url: cGif });
      setComments((c) => [data, ...(c || [])]); setR((x) => ({ ...x, comment_count: (x.comment_count || 0) + 1 })); setCText(""); setCGif(null);
    } catch (e) { toast.error(errText(e)); }
  };

  useEffect(() => {
    const el = wrap.current; const v = vref.current; if (!el || !v) return;
    const ob = new IntersectionObserver((e) => {
      if (e[0].isIntersecting) { v.play().catch(() => {}); if (!viewed.current) { viewed.current = true; api.post(`/reels/${r.id}/view`).catch(() => {}); } }
      else v.pause();
    }, { threshold: 0.6 });
    ob.observe(el); return () => ob.disconnect();
  }, [r.id]);

  useEffect(() => { if (vref.current) vref.current.muted = muted; }, [muted]);

  const like = async () => { setR((x) => ({ ...x, liked: !x.liked, like_count: x.like_count + (x.liked ? -1 : 1) })); try { await api.post(`/reels/${r.id}/like`); } catch (e) { toast.error(errText(e)); } };
  const shareToFeed = async () => {
    try { await api.post("/posts", { reel_id: r.id, text: "" }); toast.success("Shared to your feed"); } catch (e) { toast.error(errText(e)); }
  };
  const shareToStory = async () => {
    try {
      await api.post("/stories", { media_url: r.video_url, media_type: "repost", text: r.caption || "",
        shared_ref: { kind: "reel", id: r.id, author: r.author, text: r.caption || "", media_url: r.video_url, media_type: "video" } });
      toast.success("Shared to your VirtuStars Story");
      window.dispatchEvent(new Event("vs:refresh"));
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div ref={wrap} className="h-screen w-full snap-start relative flex items-center justify-center" data-testid={`reel-${r.id}`}>
      <video ref={vref} src={mediaUrl(r.video_url)} loop playsInline muted={muted} className="h-full w-full object-cover md:object-contain max-w-[500px] mx-auto" onClick={(e) => e.target.paused ? e.target.play() : e.target.pause()} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />
      <div className="absolute bottom-24 md:bottom-8 left-4 right-20 z-10">
        <Link to={`/${r.author?.username}`} className="flex items-center gap-2 mb-2"><Avatar src={r.author?.avatar_url} name={r.author?.display_name} size={40} ring /><span className="font-semibold"><CreatorName user={r.author} /></span></Link>
        <p className="text-sm mb-2">{r.caption}</p>
        {r.music && <div className="flex items-center gap-1.5 text-xs text-white/80"><Music2 size={13} /> {r.music}</div>}
      </div>
      <div className="absolute bottom-24 md:bottom-8 right-3 z-10 flex flex-col items-center gap-5">
        <Action icon={Heart} count={r.like_count} active={r.liked} onClick={like} testid={`reel-like-${r.id}`} />
        <Action icon={MessageCircle} count={r.comment_count} onClick={openComments} testid={`reel-comment-${r.id}`} />
        <Action icon={Repeat2} count={r.view_count} label="views" />
        <Action icon={Coins} label="tip" onClick={() => setTipOpen(true)} testid={`reel-tip-${r.id}`} />
        {r.author?.username && <Action icon={PlusCircle} label="story" onClick={shareToStory} testid={`reel-story-${r.id}`} />}
        <Action icon={Share2} label="feed" onClick={shareToFeed} testid={`reel-share-feed-${r.id}`} />
      </div>
      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} recipient={r.author} refType="reel" refId={r.id} />

      {showComments && (
        <div className="absolute inset-0 z-20 flex flex-col justify-end" data-testid={`reel-comments-${r.id}`}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowComments(false)} />
          <div className="relative bg-[#0a0a0f] border-t border-white/10 rounded-t-2xl max-h-[70%] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <span className="font-display font-semibold text-sm">{formatNum(r.comment_count || 0)} comments</span>
              <button onClick={() => setShowComments(false)} data-testid={`reel-comments-close-${r.id}`}><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {comments === null ? <Loading /> : comments.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Be the first to comment</p> : comments.map((c) => (
                <div key={c.id} className="flex gap-2.5">
                  <Avatar src={c.author?.avatar_url} name={c.author?.display_name} size={30} />
                  <div className="surface-2 rounded-2xl px-3 py-2 flex-1 min-w-0">
                    <Link to={`/${c.author?.username}`} className="text-xs font-semibold hover:text-eblue">{c.author?.display_name}</Link>
                    {c.text && <p className="text-sm whitespace-pre-wrap">{renderMentions(c.text)}</p>}
                    {c.gif_url && <img src={c.gif_url} alt="" className="mt-1.5 max-h-40 rounded-lg" loading="lazy" />}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-white/8 space-y-2">
              {cGif && <div className="relative w-fit"><img src={cGif} alt="" className="max-h-24 rounded-lg" /><button onClick={() => setCGif(null)} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center"><X size={13} /></button></div>}
              <div className="flex gap-2 items-center">
                <MentionInput value={cText} onChange={setCText} onEnter={addComment} multiline={false} placeholder="Add a comment… use @ to mention" className="flex-1 surface-2 border border-white/10 rounded-full px-4 py-2 text-sm outline-none" testid={`reel-comment-input-${r.id}`} />
                <button onClick={() => setGifOpen(true)} data-testid={`reel-comment-gif-${r.id}`} className="shrink-0 px-2.5 h-10 rounded-full surface-2 border border-white/10 text-[10px] font-bold tracking-wide text-eblue flex items-center">GIF</button>
                <button onClick={addComment} data-testid={`reel-comment-send-${r.id}`} className="w-10 h-10 rounded-full btn-blue flex items-center justify-center shrink-0"><Send size={16} /></button>
              </div>
            </div>
          </div>
          <GifPicker open={gifOpen} onClose={() => setGifOpen(false)} onSelect={(url) => setCGif(url)} />
        </div>
      )}
    </div>
  );
}

function Action({ icon: Icon, count, active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1">
      <span className={`w-11 h-11 rounded-full glass flex items-center justify-center ${active ? "text-rose-400" : "text-white"}`}><Icon size={22} className={active ? "fill-rose-400" : ""} /></span>
      {(count != null || label) && <span className="text-[11px] font-mono">{count != null ? formatNum(count) : label}</span>}
    </button>
  );
}
